import { paths } from '@common/types';
import { axiosInstance } from '@config/axios/axios.config';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { omit } from 'lodash';
import { AxiosResponse } from 'axios';

interface IResetUserPasswordProps {
  id: string;
}

const resetUserPasswordRequest = async ({
  id,
  ...props
}: paths['/user/{user_id}/reset-password']['post']['requestBody']['content']['application/json'] & {
  id: string;
}) => {
  const response = axiosInstance.post<
    paths['/user/{user_id}/reset-password']['post']['requestBody']['content']['application/json']
  >(`/user/${id}/reset-password`, {
    ...props,
  });
  const data = await response;

  return data;
};

export const useResetUserPasswordMutation = ({
  id,
}: IResetUserPasswordProps) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['resetUserPassword'],
    mutationFn: (
      props: paths['/user/{user_id}/reset-password']['post']['requestBody']['content']['application/json'],
    ) => resetUserPasswordRequest({ ...props, id }),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: ['user', id] }),
        client.invalidateQueries({ queryKey: ['user', 'history', id] }),
        client.invalidateQueries({ queryKey: ['users'] }),
      ]),
  });

  return mutation;
};

interface IResendUserInviteProps {
  id: string;
}

const resendUserInviteRequest = async ({ id }: IResendUserInviteProps) => {
  const response = axiosInstance.post<
    paths['/user/{user_id}/resend-invite']['post']['requestBody']
  >(`/user/${id}/resend-invite`);
  const data = await response;

  return data;
};

export const useResendUserInviteMutation = ({ id }: IResendUserInviteProps) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['resendUserInvite'],
    mutationFn: () => resendUserInviteRequest({ id }),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: ['user', id] }),
        client.invalidateQueries({ queryKey: ['user', 'history', id] }),
        client.invalidateQueries({ queryKey: ['users'] }),
      ]),
  });

  return mutation;
};

interface IDeactivateUserProps {
  id: string;
  transfer_to_user_id?: string;
}

const deactivateUserRequest = async ({
  id,
  transfer_to_user_id,
}: IDeactivateUserProps) => {
  const response = axiosInstance.delete(`/user/${id}`, {
    params: {
      ...(transfer_to_user_id && { transfer_to_user_id }),
    },
  });
  const data = await response;

  return data;
};

export const useDeactivateUserMutation = ({ id }: IDeactivateUserProps) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['deactivateUser'],
    mutationFn: (transfer_to_user_id: string) =>
      deactivateUserRequest({ id, transfer_to_user_id }),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: ['user', id] }),
        client.invalidateQueries({ queryKey: ['users'] }),
      ]),
  });

  return mutation;
};

///////////// REACTIVATE USER ////////////////////

const reactivateUserRequest = async ({ id }: IDeactivateUserProps) => {
  const response = axiosInstance.post(`/user/${id}/reactivate`);
  const data = await response;

  return data;
};

export const useReactivateUserMutation = ({ id }: IDeactivateUserProps) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['reactivateUser'],
    mutationFn: () => reactivateUserRequest({ id }),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: ['user', id] }),
        client.invalidateQueries({ queryKey: ['users'] }),
      ]),
  });

  return mutation;
};

const createUserRequest = async (
  props: paths['/user']['post']['requestBody']['content']['application/json'],
) => {
  const response = axiosInstance.post<
    paths['/user']['post']['requestBody']['content']['application/json'],
    AxiosResponse<
      paths['/user']['post']['responses']['201']['content']['application/json']
    >
  >(`/user`, {
    ...props,
  });
  const data = await response;

  return data;
};

export const useCreateUserMutation = () => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['user'],
    mutationFn: (
      props: paths['/user']['post']['requestBody']['content']['application/json'],
    ) => createUserRequest(props),
    onSuccess: () => client.invalidateQueries({ queryKey: ['users'] }),
  });

  return mutation;
};

const editUserRequest = async (
  props: paths['/user/{user_id}']['patch']['requestBody']['content']['application/json'] & {
    user_id: string;
  },
) => {
  const response = axiosInstance.patch<
    paths['/user/{user_id}']['patch']['requestBody']['content']['application/json']
  >(`/user/${props.user_id}`, {
    ...omit(props, 'user_id'),
  });
  const data = await response;

  return data;
};

export const useEditUserMutation = (user_id: string) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['editUser'],
    mutationFn: (
      props: paths['/user/{user_id}']['patch']['requestBody']['content']['application/json'] & {
        user_id: string;
      },
    ) => editUserRequest(props),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: ['users'] }),
        client.invalidateQueries({ queryKey: ['team'] }),
        client.invalidateQueries({
          queryKey: ['user', user_id],
        }),
        client.invalidateQueries({
          queryKey: ['user', 'history', user_id],
        }),
      ]),
  });

  return mutation;
};

export const usersCsvDownloadRequest = async (
  props: paths['/record/users/csv']['get']['parameters']['query'],
) => {
  const response = await axiosInstance.get(`/record/users/csv`, {
    headers: {
      'Content-type': 'application/xml',
    },
    responseType: 'blob',
    params: props,
  });

  const data = (await response).data;

  return data;
};

export const useUsersCsvDownloadQuery = () => {
  const mutation = useMutation({
    mutationKey: ['usersCSVDownload'],
    mutationFn: (
      props: paths['/record/users/csv']['get']['parameters']['query'],
    ) => usersCsvDownloadRequest(props),
  });

  return mutation;
};

// Activity log

const addActivityLogRequest = async (
  props: paths['/activities']['post']['requestBody']['content']['application/json'],
) => {
  const response = axiosInstance.post<
    paths['/activities']['post']['responses']['201']['content']
  >(`/activities`, props);
  const data = await response;

  return data;
};

export const useAddActivityLogMutation = () => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['addActivityLog'],
    mutationFn: (
      props: paths['/activities']['post']['requestBody']['content']['application/json'],
    ) => addActivityLogRequest(props),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({
          queryKey: ['activityLog'],
        }),
      ]),
  });

  return mutation;
};

const editActivityLogRequest = async (
  activity_id: string,
  props: paths['/activities/{activity_id}']['patch']['requestBody']['content']['application/json'],
) => {
  const response = axiosInstance.patch<
    paths['/activities/{activity_id}']['patch']['responses']['200']['content']
  >(`/activities/${activity_id}`, props);
  const data = await response;

  return data;
};

export const useEditActivityLogMutation = (activity_id: string) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['editActivityLog'],
    mutationFn: (
      props: paths['/activities/{activity_id}']['patch']['requestBody']['content']['application/json'],
    ) => editActivityLogRequest(activity_id, props),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({
          queryKey: ['activityLog'],
        }),
      ]),
  });

  return mutation;
};

const deleteActivityLogRequest = async (activity_id: string) => {
  const response = axiosInstance.delete<
    paths['/activities/{activity_id}']['delete']['responses']['200']['content']
  >(`/activities/${activity_id}`);
  const data = await response;

  return data;
};

export const useDeleteActivityLogMutation = (activity_id: string) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['deleteActivityLog'],
    mutationFn: () => deleteActivityLogRequest(activity_id),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({
          queryKey: ['activityLog'],
        }),
      ]),
  });

  return mutation;
};

const addTimeOffRequest = async (
  props: paths['/user-time-off']['post']['requestBody']['content']['application/json'],
) => {
  const response = axiosInstance.post<
    paths['/user-time-off']['post']['responses']['201']['content']
  >(`/user-time-off`, props);
  const data = await response;

  return data;
};

export const useAddTimeOffMutation = () => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['addTimeOff'],
    mutationFn: (
      props: paths['/user-time-off']['post']['requestBody']['content']['application/json'],
    ) => addTimeOffRequest(props),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({
          queryKey: ['timeOff', 'list'],
        }),
      ]),
  });

  return mutation;
};

const editTimeOffRequest = async (
  time_off_id: string,
  props: paths['/user-time-off/{time_off_id}']['put']['requestBody']['content']['application/json'],
) => {
  const response = axiosInstance.put<
    paths['/user-time-off/{time_off_id}']['put']['responses']['200']['content']
  >(`/user-time-off/${time_off_id}`, props);
  const data = await response;

  return data;
};

export const useEditTimeOffMutation = (time_off_id: string) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['editTimeOff'],
    mutationFn: (
      props: paths['/user-time-off/{time_off_id}']['put']['requestBody']['content']['application/json'],
    ) => editTimeOffRequest(time_off_id, props),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({
          queryKey: ['timeOff', time_off_id],
        }),
        client.invalidateQueries({
          queryKey: ['timeOff', 'list'],
        }),
      ]),
  });

  return mutation;
};

const deleteTimeOffRequest = async (time_off_id: string) => {
  const response = axiosInstance.delete<
    paths['/user-time-off/{time_off_id}']['delete']['responses']['200']['content']
  >(`/user-time-off/${time_off_id}`);
  const data = await response;

  return data;
};

export const useDeleteTimeOffMutation = (time_off_id: string) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['deleteTimeOff'],
    mutationFn: () => deleteTimeOffRequest(time_off_id),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({
          queryKey: ['timeOff', 'list'],
        }),
        client.invalidateQueries({
          queryKey: ['timeOff', time_off_id],
        }),
      ]),
  });

  return mutation;
};
